// The privacy notice on the client (Session ASQ, Stream G, Item 5).
//
// THE RULE: until an admin saves text in Settings, the /privacy page and every link to it stay hidden; nothing ever
// shows a default or placeholder notice. The server answers GET /api/public/privacy-notice with 404 not_published
// until then, and this module turns that into one of three states.
//
// ONE REQUEST PER PAGE LOAD. A page can carry several links (a footer, a portal sign-in) and the /privacy page itself
// reads the same answer, so the first caller starts the request and every later one shares it. Nothing is kept past
// the page load: a notice the admin clears is gone on the next load.
//
// NEVER THROWS, NEVER LEAVES A REJECTION BEHIND. A failed read is an "error" state (a link then shows nothing: a link
// is only offered once we know the page exists). Everything, including reaching the api module and reading the error,
// sits inside the try, because a page test that mocks the api module without this call must still render.
import { api } from "./api";

export type PrivacyNoticeState =
  | { status: "published"; text: string; updatedAt: string | null }
  | { status: "unpublished" }
  | { status: "error" };

let pending: Promise<PrivacyNoticeState> | null = null;

async function fetchState(): Promise<PrivacyNoticeState> {
  try {
    const notice = await api.public.privacyNotice();
    const text = typeof notice?.text === "string" ? notice.text : "";
    if (!/\S/.test(text)) return { status: "unpublished" };
    return { status: "published", text, updatedAt: typeof notice.updatedAt === "string" ? notice.updatedAt : null };
  } catch (err) {
    try {
      return (err as { status?: unknown } | null)?.status === 404 ? { status: "unpublished" } : { status: "error" };
    } catch {
      return { status: "error" };
    }
  }
}

export function loadPrivacyNotice(): Promise<PrivacyNoticeState> {
  if (!pending) pending = fetchState();
  return pending;
}

// Tests only: forget this page load's answer.
export function resetPrivacyNoticeCache(): void {
  pending = null;
}

// "Last updated DD/MM/YYYY", on the London calendar day (a notice saved at 00:30 BST is that day, not the day before).
export function formatNoticeDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/London" }).format(d);
}

// The places the notice is linked from once it is saved (Settings states this list to the admin).
export const PRIVACY_NOTICE_LINKED_FROM =
  "the customer portal (its sign-in page and every page after it), subcontractor onboarding, the quote and renewal pages, the NPS survey and the footer of every branded email";
